import { Component, useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { RefreshCw, Printer, FileDown, FileText, Lock } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/*
  Documenti dell'assemblea — registro delle presenze, traccia di verbale, relazione.

  I documenti stanno sul server e arrivano da una rotta protetta: contengono i nomi
  dei condòmini e i loro millesimi, e solo l'amministratore del condominio li ottiene.
  Per questo NON sono file statici: un file statico è raggiungibile da chiunque ne
  indovini l'indirizzo, senza autenticazione.

  Un iframe non può puntare a una rotta protetta, perché non manda l'intestazione
  Authorization. Quindi la pagina scarica il documento con fetch, token compreso, e lo
  scrive nell'iframe con `srcdoc`. Il registro resta interattivo: le spunte e il
  conteggio dei millesimi girano dentro l'iframe come nel file locale.

  L'anteprima di stampa stampa **l'iframe**, non questa pagina: così esce il documento
  impaginato in A4, senza il menù dell'applicazione intorno.
*/

const DOC_ICONE = { registro: FileText, verbale: FileText, relazione: FileText };

/* Se qualcosa qui dentro esplode non deve portarsi via il resto dell'applicazione. */
class Recinto extends Component {
  constructor(p) { super(p); this.state = { rotto: false }; }
  static getDerivedStateFromError() { return { rotto: true }; }
  render() {
    if (this.state.rotto) {
      return (
        <Card><CardContent className="pt-5 text-sm text-muted-foreground">
          La pagina dei documenti non è riuscita a disegnarsi. I file restano
          disponibili: riprova con «Ricarica».
        </CardContent></Card>
      );
    }
    return this.props.children;
  }
}

const Contenuto = () => {
  const { token, selectedCondominium } = useAuth();
  const condoId = selectedCondominium?.condominium?.id || selectedCondominium?.id;
  const [elenco, setElenco] = useState([]);
  const [attivo, setAttivo] = useState(null);
  const [html, setHtml] = useState('');
  const [loading, setLoading] = useState(true);
  const [caricandoDoc, setCaricandoDoc] = useState(false);
  const [errore, setErrore] = useState(null);
  const [negato, setNegato] = useState(false);
  const iframeRef = useRef(null);

  const base = `${API_URL}/api/condominiums/${condoId}/assemblea`;

  const carica = useCallback(async () => {
    if (!condoId) { setLoading(false); return; }
    setLoading(true); setErrore(null); setNegato(false);
    try {
      const r = await fetch(base, { headers: { Authorization: `Bearer ${token}` } });
      if (r.status === 403) { setNegato(true); setElenco([]); return; }
      if (!r.ok) throw new Error('elenco');
      const d = await r.json();
      const docs = d?.documenti || [];
      setElenco(docs);
      const primo = docs.find((x) => x.disponibile);
      setAttivo((a) => a || (primo ? primo.chiave : null));
    } catch (e) {
      setErrore('Non sono riuscito a leggere l’elenco dei documenti.');
    } finally {
      setLoading(false);
    }
  }, [base, condoId, token]);

  useEffect(() => { carica(); }, [carica]);

  useEffect(() => {
    let vivo = true;
    if (!attivo || !condoId) return undefined;
    setCaricandoDoc(true);
    fetch(`${base}/${attivo}`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => (r.ok ? r.text() : Promise.reject(new Error(String(r.status)))))
      .then((t) => { if (vivo) { setHtml(t); setErrore(null); } })
      .catch(() => { if (vivo) setErrore('Non sono riuscito a leggere il documento.'); })
      .finally(() => { if (vivo) setCaricandoDoc(false); });
    return () => { vivo = false; };
  }, [attivo, base, condoId, token]);

  const stampa = () => {
    const f = iframeRef.current;
    if (!f || !f.contentWindow) return;
    f.contentWindow.focus();
    f.contentWindow.print();
  };

  const scaricaWord = async () => {
    if (!attivo) return;
    try {
      const r = await fetch(`${base}/${attivo}/word`,
        { headers: { Authorization: `Bearer ${token}` } });
      if (!r.ok) throw new Error(String(r.status));
      const blob = await r.blob();
      const nome = (r.headers.get('content-disposition') || '')
        .match(/filename="?([^";]+)"?/)?.[1] || `${attivo}.docx`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = nome;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 4000);
    } catch (e) {
      setErrore('Il file Word non è disponibile per questo documento.');
    }
  };

  if (!condoId) {
    return <p className="text-sm text-muted-foreground">Seleziona prima un condominio.</p>;
  }

  if (negato) {
    return (
      <Card><CardContent className="pt-5 flex items-start gap-3">
        <Lock className="w-4 h-4 mt-0.5 text-muted-foreground" />
        <p className="text-sm text-muted-foreground">
          I documenti dell’assemblea sono riservati all’amministratore del condominio.
        </p>
      </CardContent></Card>
    );
  }

  const corrente = elenco.find((x) => x.chiave === attivo);

  return (
    <div className="space-y-5">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="text-lg font-semibold tracking-tight">Documenti dell’assemblea</h2>
          <p className="text-sm text-muted-foreground">
            Registro delle presenze, traccia di verbale e relazione. Si consultano qui,
            si stampano in A4 e si scaricano in Word con l’intestazione del Condominio.
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" size="sm" onClick={carica} disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Ricarica
          </Button>
          <Button variant="outline" size="sm" onClick={stampa}
                  disabled={!html || caricandoDoc}>
            <Printer className="w-4 h-4 mr-2" />
            Anteprima di stampa
          </Button>
          <Button size="sm" onClick={scaricaWord}
                  disabled={!corrente?.word_disponibile}>
            <FileDown className="w-4 h-4 mr-2" />
            Scarica Word
          </Button>
        </div>
      </div>

      {errore && <p className="text-sm text-red-700">{errore}</p>}
      {loading && <p className="text-sm text-muted-foreground">Lettura in corso&hellip;</p>}

      {!loading && (
        <>
          <div className="grid gap-3 sm:grid-cols-3">
            {elenco.map((d) => {
              const Icona = DOC_ICONE[d.chiave] || FileText;
              const scelto = d.chiave === attivo;
              return (
                <button key={d.chiave} type="button"
                        onClick={() => d.disponibile && setAttivo(d.chiave)}
                        disabled={!d.disponibile}
                        className={`text-left border rounded-md p-3.5 transition-colors ${
                          scelto ? 'border-2' : 'hover:bg-muted/40'
                        } ${d.disponibile ? '' : 'opacity-50 cursor-not-allowed'}`}
                        style={scelto ? { borderColor: '#0D4D4F' } : undefined}>
                  <div className="flex items-center gap-2 mb-1">
                    <Icona className="w-4 h-4" />
                    <span className="font-medium text-sm">{d.titolo}</span>
                  </div>
                  <p className="text-xs text-muted-foreground">{d.descrizione}</p>
                  <div className="mt-2 flex gap-1.5 flex-wrap">
                    {d.disponibile
                      ? <Badge variant="secondary">pronto</Badge>
                      : <Badge variant="outline">non pubblicato</Badge>}
                    {d.word_disponibile && <Badge variant="outline">Word</Badge>}
                  </div>
                </button>
              );
            })}
          </div>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">
                {corrente ? corrente.titolo : 'Documento'}
              </CardTitle>
              <CardDescription>
                {caricandoDoc
                  ? 'Caricamento del documento…'
                  : 'Il documento è mostrato come verrà stampato. Il registro delle '
                    + 'presenze si compila anche qui: spuntando «presente» o «delegato» '
                    + 'i millesimi si sommano da soli.'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <iframe
                ref={iframeRef}
                title="Documento dell'assemblea"
                srcDoc={html}
                className="w-full border rounded-md bg-white"
                style={{ height: '78vh' }}
              />
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
};

export const AssembleaSezione = () => (
  <Recinto><Contenuto /></Recinto>
);

export const Assemblea = () => (
  <Recinto><Contenuto /></Recinto>
);

export default Assemblea;
